import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Grid,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { format } from "date-fns";
import useAdminReturns from "../../hooks/useAdminReturns";

// Helper to get chip color based on return status
const getReturnStatusChipColor = (status) => {
  switch (status?.toLowerCase()) {
    case "requested":
      return "warning";
    case "under_review":
      return "info";
    case "approved":
    case "partially_approved":
      return "secondary";
    case "shipped_by_user":
      return "info";
    case "received":
      return "primary";
    case "completed":
      return "success";
    case "rejected":
      return "error";
    default:
      return "default";
  }
};

/**
 * Admin Returns Page component
 * Displays list of all returns with search, filter, and pagination
 * @returns {JSX.Element} AdminReturnsPage component
 */
const AdminReturnsPage = () => {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("returns");

  const {
    returns,
    totalReturns,
    status,
    error,
    currentPage,
    rowsPerPage,
    searchTerm,
    statusFilter,
    setCurrentPage,
    setRowsPerPage,
    setSearchTerm,
    setStatusFilter,
  } = useAdminReturns();

  // Log current state for debugging
  useEffect(() => {
    console.log("AdminReturnsPage - Current state:", {
      returns: returns.length,
      totalReturns,
      status,
      searchTerm,
      statusFilter,
      currentPage,
    });
  }, [returns, totalReturns, status, searchTerm, statusFilter, currentPage]);

  const handleChangePage = (event, newPage) => {
    console.log("AdminReturnsPage - handleChangePage:", newPage);
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10);
    console.log("AdminReturnsPage - handleChangeRowsPerPage:", value);
    setRowsPerPage(value);
    setCurrentPage(0);
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    console.log("AdminReturnsPage - handleSearchChange:", value);
    setSearchTerm(value);
    setCurrentPage(0);
  };

  const handleStatusFilterChange = (event) => {
    const value = event.target.value;
    console.log("AdminReturnsPage - handleStatusFilterChange:", value);
    setStatusFilter(value);
    setCurrentPage(0);
  };

  // Determine if data is loading
  const isLoading = status === "loading";

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t("admin.list.title")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error?.message || tCommon("status.error")}
        </Alert>
      )}

      {/* Filters */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { md: "center" },
          gap: 2,
        }}
      >
        <TextField
          label={t("admin.list.filters.search")}
          variant="outlined"
          size="small"
          value={searchTerm || ""}
          onChange={handleSearchChange}
          sx={{ flex: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          placeholder={t("admin.list.filters.searchPlaceholder")}
        />

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="status-filter-label">
            {t("admin.list.filters.status")}
          </InputLabel>
          <Select
            labelId="status-filter-label"
            id="status-filter"
            value={statusFilter || ""}
            label={t("admin.list.filters.status")}
            onChange={handleStatusFilterChange}
          >
            <MenuItem value="">{t("admin.list.filters.allStatuses")}</MenuItem>
            <MenuItem value="requested">
              {t("shared.status.requested")}
            </MenuItem>
            <MenuItem value="under_review">
              {t("shared.status.under_review")}
            </MenuItem>
            <MenuItem value="approved">{t("shared.status.approved")}</MenuItem>
            <MenuItem value="partially_approved">
              {t("shared.status.partially_approved")}
            </MenuItem>
            <MenuItem value="shipped_by_user">
              {t("shared.status.shipped_by_user")}
            </MenuItem>
            <MenuItem value="received">{t("shared.status.received")}</MenuItem>
            <MenuItem value="completed">
              {t("shared.status.completed")}
            </MenuItem>
            <MenuItem value="rejected">{t("shared.status.rejected")}</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {/* Returns Table */}
      <Paper sx={{ width: "100%" }}>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t("admin.list.table.returnId")}</TableCell>
                <TableCell>{t("admin.list.table.orderNumber")}</TableCell>
                <TableCell>{t("admin.list.table.customer")}</TableCell>
                <TableCell>{t("admin.list.table.dateRequested")}</TableCell>
                <TableCell>{t("admin.list.table.status")}</TableCell>
                <TableCell align="center">
                  {t("admin.list.table.itemCount")}
                </TableCell>
                <TableCell>{t("admin.list.table.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={30} />
                  </TableCell>
                </TableRow>
              ) : returns.length > 0 ? (
                returns.map((returnItem) => (
                  <TableRow key={returnItem.id}>
                    <TableCell>{returnItem.id}</TableCell>
                    <TableCell>
                      {returnItem.order?.orderNumber || returnItem.orderId}
                    </TableCell>
                    <TableCell>
                      {returnItem.user?.firstName
                        ? `${returnItem.user.firstName} ${returnItem.user.lastName}`
                        : returnItem.userId ||
                          t("admin.list.table.unknownCustomer")}
                    </TableCell>
                    <TableCell>
                      {returnItem.createdAt
                        ? format(new Date(returnItem.createdAt), "PP")
                        : tCommon("status.notApplicable", "N/A")}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t(
                          `shared.status.${returnItem.status?.toLowerCase()}`,
                          returnItem.status || t("shared.status.unknown")
                        )}
                        color={getReturnStatusChipColor(returnItem.status)}
                        size="small"
                        sx={{ textTransform: "capitalize" }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {returnItem.items ? returnItem.items.length : 0}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        component={RouterLink}
                        to={`/admin/returns/${returnItem.id}`}
                        aria-label={t("admin.list.table.viewDetails")}
                        color="primary"
                        size="small"
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    {searchTerm || statusFilter
                      ? t("admin.list.table.noMatchingReturns")
                      : t("admin.list.table.noReturns")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalReturns || 0}
          rowsPerPage={rowsPerPage}
          page={currentPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={tCommon("pagination.rowsPerPage")}
          labelDisplayedRows={({ from, to, count }) =>
            tCommon("pagination.displayedRows", { from, to, count })
          }
        />
      </Paper>
    </Container>
  );
};

export default AdminReturnsPage;
